import { useCallback } from 'react'
import { doc, setDoc } from 'firebase/firestore'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'
import type { UserSettings } from './useUserSettings'

/**
 * Settings writes from the PWA into users/{uid}/settings/frontend.
 * Mirrors the desktop's syncSettingUpdate path so a key set from the
 * phone shows up on every other device the same way as a desktop edit.
 *
 * The returned `updateSetting` is strongly typed against UserSettings so
 * the panel components get autocomplete and type checking.
 */
export function useWriteSettings() {
  const { user } = useAuth()

  const updateSetting = useCallback(
    async <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
      if (!user) return
      const ref = doc(firestore, `users/${user.uid}/settings/frontend`)
      await setDoc(
        ref,
        {
          [key]: value,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      )
    },
    [user]
  )

  return { updateSetting }
}
